// =============================================================================
// Admin Service
// =============================================================================
// API client for admin portal functionality
// =============================================================================

import api from './api';
import type {
  Deal,
  DocumentType,
  PropertyType,
} from '../types';

// =============================================================================
// Types
// =============================================================================

export interface AdminDashboardStats {
  totalInvestors: number;
  activeDeals: number;
  pendingDocuments: number;
  recentDistributions: number;
  tempAccessGrants: number;
  securityAlerts: number;
}

export interface InvestorWithProfiles {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name: string;
  phone?: string;
  phoneVerified?: boolean;
  sms2faEnabled?: boolean;
  totpEnabled?: boolean;
  lastLoginAt?: string;
  createdAt: string;
  profiles: {
    id: string;
    type: string;
    legalName?: string;
    entityName?: string;
    entityType?: string;
    stateOfFormation?: string;
    authorizedSigner?: string;
    ssnMasked?: string;
    feinMasked?: string;
    bankName?: string;
    bankAccountType?: string;
    routingNumberMasked?: string;
    accountNumberMasked?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    investmentCount: number;
    totalInvested: number;
  }[];
}

export interface InvestmentContribution {
  id: string;
  amount: number;
  fundingDate: string;
  notes?: string;
  createdAt: string;
}

export interface InvestorDetails extends InvestorWithProfiles {
  investments: {
    id: string;
    dealId: string;
    dealName: string;
    profileId: string;
    profileName: string;
    investmentAmount: number;
    ownershipPercentage: number;
    totalDistributed: number;
    isActive: boolean;
    shareClassId?: string | null;
    shareClassName?: string | null;
    shareClassCode?: string | null;
    contributions?: InvestmentContribution[];
  }[];
}

export type DealStatus = 'FUNDRAISING' | 'ACTIVE' | 'CLOSED';

export interface DealWithStats {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  propertyType: PropertyType;
  status: DealStatus;
  targetRaise?: number;
  currentRaised?: number;
  totalEquityRaised: number;
  acquisitionDate?: string;
  description?: string;
  isActive: boolean;
  investorCount: number;
  totalInvested: number;
  totalDistributed: number;
  imageUrl?: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface AuditLogEntry {
  id: string;
  type: string;
  action: string;
  userId?: string;
  userName?: string;
  userEmail?: string;
  targetType?: string;
  targetId?: string;
  details?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
}

export interface TempAccessUser {
  id: string;
  email: string;
  name: string;
  createdAt: string;
  lastLoginAt?: string;
  // Grant details
  grantId?: string;
  accessType?: 'ACCOUNTANT' | 'AUDITOR' | 'CUSTOM';
  expiresAt?: string;
  canViewSsn?: boolean;
  canViewBanking?: boolean;
  grantedBy?: string;
  // Deal access
  deals?: { id: string; name: string }[];
  dealCount?: number;
}

export interface CreateInvestorInput {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  sendWelcomeEmail?: boolean;
}

export interface CreateDealInput {
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  propertyType: PropertyType;
  status?: DealStatus;
  targetRaise?: number;
  totalEquityRaised: number;
  acquisitionDate?: string;
  description?: string;
  isActive?: boolean;
}

export interface UpdateDealInput {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  propertyType?: PropertyType;
  status?: DealStatus;
  targetRaise?: number;
  totalEquityRaised?: number;
  acquisitionDate?: string;
  description?: string;
  isActive?: boolean;
  // PE/RE Fund Terms
  fundFormationDate?: string;
  investmentPeriodStart?: string;
  investmentPeriodEnd?: string;
  fundTermYears?: number;
  fundTermEndDate?: string;
  extensionYears?: number;
  extensionsUsed?: number;
  windDownPeriodMonths?: number;
  // Fee Structure
  managementFeeRate?: number;
  managementFeeBasis?: string;
  managementFeeStartDate?: string;
  managementFeeReducedRate?: number;
  acquisitionFeeRate?: number;
  dispositionFeeRate?: number;
  assetManagementFeeRate?: number;
  feeOffsetEnabled?: boolean;
  feeOffsetPercent?: number;
  orgExpensesCap?: number;
  orgExpensesAmortized?: boolean;
  // Capital Management
  totalCommittedCapital?: number;
  totalCalledCapital?: number;
  recyclingEnabled?: boolean;
  recyclingPeriodEnd?: string;
  recyclingLimit?: number;
  // Clawback & Lookback
  clawbackEnabled?: boolean;
  lookbackEnabled?: boolean;
  // Key Person Provisions
  keyPersonsEnabled?: boolean;
  keyPersonNames?: string[];
  keyPersonTriggered?: boolean;
  keyPersonTriggerDate?: string;
  // Custom fields
  customFields?: { key: string; value: string }[];
  // Waterfall configuration
  waterfallType?: 'AMERICAN' | 'EUROPEAN';
}

export interface CreateTempAccessInput {
  email: string;
  name: string;
  expiresInDays?: number;
  accessType?: 'ACCOUNTANT' | 'AUDITOR' | 'CUSTOM';
  dealIds?: string[]; // Empty array = all deals
  canViewSsn?: boolean;
  canViewBanking?: boolean;
  sendEmail?: boolean;
}

export interface UpdateTempAccessInput {
  expiresInDays?: number;
  dealIds?: string[]; // Empty array = all deals
  canViewSsn?: boolean;
  canViewBanking?: boolean;
}

export interface AdminDocument {
  id: string;
  name: string;
  type: DocumentType;
  description?: string;
  fileSize?: number;
  mimeType?: string;
  deal?: { id: string; name: string };
  investorProfile?: {
    id: string;
    name: string;
    userName: string;
    userEmail: string;
  } | null;
  uploadedBy?: { id: string; name: string; email: string };
  accessLevels?: string[];
  visibleToCount?: number;
  visibleToNames?: string[];
  createdAt: string;
}

export interface InvestorDistribution {
  id: string;
  distributionDate: string;
  dealId: string;
  dealName: string;
  type: string;
  totalAmount: number;
  investorAmount: number;
  ownershipPercentage: number;
  paymentMethod: string;
  status: 'DRAFT' | 'PUBLISHED';
  profileId: string;
  profileName: string;
  profileType: string;
}

export interface InvestorDocument {
  id: string;
  name: string;
  type: string;
  description?: string;
  fileSize?: number;
  mimeType?: string;
  deal?: { id: string; name: string };
  investorProfile?: {
    id: string;
    name: string;
    userName: string;
    userEmail: string;
  };
  uploadedBy?: { id: string; name: string; email: string };
  accessLevels?: string[];
  isSharedToAll: boolean;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Share Class & Waterfall Types
// =============================================================================

export type HurdleType = 'IRR' | 'EQUITY_MULTIPLE' | 'CASH_ON_CASH';
export type CompoundingMethod = 'NONE' | 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
export type PreferredReturnType = 'TRUE_PREFERRED' | 'PARI_PASSU';

export interface WaterfallTier {
  id: string;
  shareClassId: string;
  tierNumber: number;
  name: string;
  hurdleType: HurdleType;
  hurdleValue?: number;
  lpSplitPercent: number;
  gpSplitPercent: number;
  gpPromotePercent: number;
  createdAt: string;
  updatedAt: string;
}

export interface ShareClass {
  id: string;
  dealId: string;
  name: string;
  code: string;
  description?: string;
  priority: number;
  isDefault: boolean;
  lpSplitPercent: number;
  gpSplitPercent: number;
  preferredReturnRate?: number;
  preferredReturnType: PreferredReturnType;
  compoundingMethod: CompoundingMethod;
  isCumulative: boolean;
  catchUpPercent?: number;
  carriedInterestRate?: number;
  maxEquityMultiple?: number | null;
  minimumInvestment?: number;
  waterfallTiers?: WaterfallTier[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateShareClassInput {
  dealId: string;
  name: string;
  code: string;
  description?: string;
  priority?: number;
  isDefault?: boolean;
  lpSplitPercent?: number;
  gpSplitPercent?: number;
  preferredReturnRate?: number;
  preferredReturnType?: PreferredReturnType;
  compoundingMethod?: CompoundingMethod;
  isCumulative?: boolean;
  catchUpPercent?: number;
  carriedInterestRate?: number;
  maxEquityMultiple?: number | null;
  minimumInvestment?: number;
}

export interface UpdateShareClassInput {
  name?: string;
  code?: string;
  description?: string;
  priority?: number;
  isDefault?: boolean;
  lpSplitPercent?: number;
  gpSplitPercent?: number;
  preferredReturnRate?: number;
  preferredReturnType?: PreferredReturnType;
  compoundingMethod?: CompoundingMethod;
  isCumulative?: boolean;
  catchUpPercent?: number;
  carriedInterestRate?: number;
  maxEquityMultiple?: number | null;
  minimumInvestment?: number;
}

export interface CreateWaterfallTierInput {
  tierNumber: number;
  name: string;
  hurdleType?: HurdleType;
  hurdleValue?: number;
  lpSplitPercent: number;
  gpSplitPercent: number;
  gpPromotePercent?: number;
}

export interface UpdateWaterfallTierInput {
  tierNumber?: number;
  name?: string;
  hurdleType?: HurdleType;
  hurdleValue?: number;
  lpSplitPercent?: number;
  gpSplitPercent?: number;
  gpPromotePercent?: number;
}

// =============================================================================
// Waterfall Analysis Types
// =============================================================================

export interface WaterfallTierBreakdown {
  tierNumber: number;
  tierName: string;
  amountIn: number;
  lpAmount: number;
  gpAmount: number;
  promoteAmount: number;
  remainingCf: number;
}

export interface WaterfallDistribution {
  id: string;
  date: string;
  type: string;
  source: string;
  totalAmount: number;
  tiers: WaterfallTierBreakdown[];
}

export interface WaterfallCashFlowTier {
  tierNumber: number;
  tierName: string;
  lpAmount: number;
  gpAmount: number;
  promoteAmount: number;
}

export interface WaterfallCashFlow {
  date: string;
  amount: number;
  type: string;
  label: string;
  tierBreakdown?: WaterfallCashFlowTier[];
}

export interface WaterfallInvestor {
  investmentId: string;
  investorName: string;
  entityName: string | null;
  shareClassName: string | null;
  investorRole: 'LP' | 'GP';
  isPromoteRecipient: boolean;
  invested: number;
  totalDistributed: number;
  lpShare: number;
  gpPromote: number;
  equityMultiple: number;
  irr: number | null;
  cashFlows: WaterfallCashFlow[];
}

export interface WaterfallCapitalSummary {
  totalEquity: number;
  totalCommitted: number;
  totalContributed: number;
  totalCapitalReturned: number;
  capitalOutstanding: number;
  unfundedCommitment: number;
  prefReturnAccrued: number;
  prefReturnPaid: number;
  prefOutstanding: number;
  catchUpPaid: number;
  residualPaid: number;
  totalDistributed: number;
  dealIrr: number | null;
  equityMultiple: number;
}

export interface WaterfallCapitalEvent {
  id: string;
  date: string;
  eventType: 'CAPITAL_CALL' | 'DISTRIBUTION';
  description: string;
  amount: number;
  tiers?: { tierNumber: number; tierName: string; amountIn: number; lpAmount: number; gpAmount: number; promoteAmount: number }[];
  runningContributed: number;
  runningDistributed: number;
  runningUnreturnedCapital: number;
  runningUnfundedCommitment: number;
}

export interface WaterfallFeeSummary {
  totalAssetManagement: number;
  totalDisposition: number;
  totalAcquisition: number;
  totalOther: number;
  totalAllFees: number;
  totalOffsets: number;
}

export interface WaterfallEscrowSummary {
  totalEscrowed: number;
  currentBalance: number;
  totalReleased: number;
  totalClawedBack: number;
  escrowPercent: number;
}

export interface EscrowBalance {
  totalEscrowed: number;
  totalReleased: number;
  totalClawedBack: number;
  currentBalance: number;
  records: Array<{
    id: string;
    distributionId: string;
    amount: number;
    status: string;
    releasedDate: string | null;
    releasedAmount: number | null;
    createdAt: string;
  }>;
}

export interface ClawbackResult {
  totalGpPromote: number;
  entitledGpPromote: number;
  clawbackAmount: number;
  escrowAvailable: number;
  escrowUsed: number;
  remainingGpObligation: number;
  lpActualIrr: number | null;
  lpTargetIrr: number;
  dealEquityMultiple: number;
  isFinal: boolean;
}

export interface LookbackResult {
  totalDistributed: number;
  gpActualTotal: number;
  lpActualTotal: number;
  gpHypotheticalTotal: number;
  lpHypotheticalTotal: number;
  gpAdjustment: number;
  lpAdjustment: number;
  isFinal: boolean;
}

export interface PreferredEquityTrancheData {
  id: string;
  dealId: string;
  name: string;
  investedAmount: number;
  preferredReturnRate: number;
  compoundingMethod: string;
  minimumEquityMultiple: number | null;
  startDate: string;
  maturityDate: string | null;
  isActive: boolean;
  outstandingBalance: number;
  totalAccrued: number;
  totalPaid: number;
  createdAt: string;
}

export interface CreatePreferredEquityInput {
  name: string;
  investedAmount: number;
  preferredReturnRate: number;
  compoundingMethod?: string;
  minimumEquityMultiple?: number | null;
  startDate: string;
  maturityDate?: string | null;
}

export interface WaterfallPrefEquitySummary {
  trancheCount: number;
  totalInvested: number;
  totalOutstanding: number;
  totalPaid: number;
  tranches: PreferredEquityTrancheData[];
}

export interface WaterfallAnalysisData {
  capitalSummary: WaterfallCapitalSummary;
  distributions: WaterfallDistribution[];
  capitalEvents: WaterfallCapitalEvent[];
  investors: WaterfallInvestor[];
  feeSummary?: WaterfallFeeSummary | null;
  escrowSummary?: WaterfallEscrowSummary | null;
  prefEquitySummary?: WaterfallPrefEquitySummary | null;
}

// =============================================================================
// Admin Service
// =============================================================================

export const adminService = {
  // ===========================================================================
  // Dashboard
  // ===========================================================================

  async getDashboard(): Promise<AdminDashboardStats> {
    const response = await api.get<{ success: boolean; data: AdminDashboardStats }>(
      '/admin/dashboard'
    );
    return response.data.data;
  },

  // ===========================================================================
  // Investors
  // ===========================================================================

  async listInvestors(options?: {
    search?: string;
    dealId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ investors: InvestorWithProfiles[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.search) params.append('search', options.search);
    if (options?.dealId) params.append('dealId', options.dealId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await api.get<{
      success: boolean;
      data: InvestorWithProfiles[];
      total: number;
    }>(`/admin/investors?${params.toString()}`);

    return { investors: response.data.data, total: response.data.total };
  },

  async getInvestor(investorId: string): Promise<InvestorDetails> {
    const response = await api.get<{ success: boolean; data: InvestorDetails }>(
      `/admin/investors/${investorId}`
    );
    return response.data.data;
  },

  async createInvestor(data: CreateInvestorInput): Promise<{ id: string; email: string; name: string; welcomeEmailSent?: boolean }> {
    const response = await api.post<{
      success: boolean;
      data: { id: string; email: string; name: string; welcomeEmailSent?: boolean };
    }>('/admin/investors', data);
    return response.data.data;
  },

  async updateInvestor(
    investorId: string,
    data: { firstName?: string; lastName?: string; phone?: string; email?: string }
  ): Promise<{ id: string; email: string; firstName?: string; lastName?: string; name: string; phone?: string }> {
    const response = await api.put<{
      success: boolean;
      data: { id: string; email: string; firstName?: string; lastName?: string; name: string; phone?: string };
    }>(`/admin/investors/${investorId}`, data);
    return response.data.data;
  },

  async deleteInvestor(investorId: string): Promise<void> {
    await api.delete(`/admin/investors/${investorId}`);
  },

  // ===========================================================================
  // Investor Profiles
  // ===========================================================================

  async createInvestorProfile(
    investorId: string,
    data: {
      type: 'INDIVIDUAL' | 'JOINT' | 'LLC' | 'TRUST' | 'IRA' | 'CORPORATION' | 'PARTNERSHIP' | 'OTHER';
      legalName?: string;
      entityName?: string;
      entityType?: string;
      stateOfFormation?: string;
      authorizedSigner?: string;
      ssn?: string;
      fein?: string;
      bankName?: string;
      bankAccountType?: 'CHECKING' | 'SAVINGS';
      routingNumber?: string;
      accountNumber?: string;
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    }
  ): Promise<{ id: string; type: string; legalName?: string; entityName?: string }> {
    const response = await api.post<{
      success: boolean;
      data: { id: string; type: string; legalName?: string; entityName?: string };
    }>(`/admin/investors/${investorId}/profiles`, data);
    return response.data.data;
  },

  async updateInvestorProfile(
    profileId: string,
    data: {
      type?: 'INDIVIDUAL' | 'JOINT' | 'LLC' | 'TRUST' | 'IRA' | 'CORPORATION' | 'PARTNERSHIP' | 'OTHER';
      legalName?: string;
      entityName?: string;
      entityType?: string;
      stateOfFormation?: string;
      authorizedSigner?: string;
      ssn?: string;
      fein?: string;
      bankName?: string;
      bankAccountType?: 'CHECKING' | 'SAVINGS';
      routingNumber?: string;
      accountNumber?: string;
      address?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    }
  ): Promise<{ id: string; type: string; legalName?: string; entityName?: string }> {
    const response = await api.put<{
      success: boolean;
      data: { id: string; type: string; legalName?: string; entityName?: string };
    }>(`/admin/profiles/${profileId}`, data);
    return response.data.data;
  },

  async deleteInvestorProfile(profileId: string): Promise<void> {
    await api.delete(`/admin/profiles/${profileId}`);
  },

  async getProfileSensitiveData(profileId: string): Promise<{
    ssn: string | null;
    fein: string | null;
    routingNumber: string | null;
    accountNumber: string | null;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        ssn: string | null;
        fein: string | null;
        routingNumber: string | null;
        accountNumber: string | null;
      };
    }>(`/admin/profiles/${profileId}/sensitive`);
    return response.data.data;
  },

  async getInvestorDistributions(investorId: string): Promise<InvestorDistribution[]> {
    const response = await api.get<{
      success: boolean;
      data: InvestorDistribution[];
    }>(`/admin/investors/${investorId}/distributions`);
    return response.data.data;
  },

  async getInvestorDocuments(investorId: string): Promise<InvestorDocument[]> {
    const response = await api.get<{
      success: boolean;
      data: InvestorDocument[];
    }>(`/admin/investors/${investorId}/documents`);
    return response.data.data;
  },

  // ===========================================================================
  // Investments
  // ===========================================================================

  async createInvestment(data: {
    profileId: string;
    dealId: string;
    investmentAmount: number;
    investmentDate?: string;
    shareClassId?: string;
    investorRole?: 'LP' | 'GP';
  }): Promise<{
    id: string;
    dealId: string;
    dealName: string;
    profileId: string;
    profileName: string;
    investmentAmount: number;
    ownershipPercentage: number;
    investmentDate: string;
  }> {
    const response = await api.post<{
      success: boolean;
      data: {
        id: string;
        dealId: string;
        dealName: string;
        profileId: string;
        profileName: string;
        investmentAmount: number;
        ownershipPercentage: number;
        investmentDate: string;
      };
    }>('/admin/investments', data);
    return response.data.data;
  },

  async updateInvestment(
    investmentId: string,
    data: {
      investmentAmount?: number;
      investmentDate?: string;
      isActive?: boolean;
      isPromoteRecipient?: boolean;
      shareClassId?: string | null;
      investorRole?: 'LP' | 'GP';
    }
  ): Promise<{
    id: string;
    dealId: string;
    dealName: string;
    profileId: string;
    profileName: string;
    investmentAmount: number;
    ownershipPercentage: number;
    investmentDate: string;
    isActive: boolean;
    isPromoteRecipient: boolean;
  }> {
    const response = await api.put<{
      success: boolean;
      data: {
        id: string;
        dealId: string;
        dealName: string;
        profileId: string;
        profileName: string;
        investmentAmount: number;
        ownershipPercentage: number;
        investmentDate: string;
        isActive: boolean;
        isPromoteRecipient: boolean;
      };
    }>(`/admin/investments/${investmentId}`, data);
    return response.data.data;
  },

  async deleteInvestment(investmentId: string): Promise<void> {
    await api.delete(`/admin/investments/${investmentId}`);
  },

  // ===========================================================================
  // Investment Contributions
  // ===========================================================================

  async addContribution(
    investmentId: string,
    data: { amount: number; fundingDate: string; notes?: string }
  ): Promise<{
    id: string;
    amount: number;
    fundingDate: string;
    notes?: string;
    investment: {
      id: string;
      investmentAmount: number;
      ownershipPercentage: number;
    };
  }> {
    const response = await api.post<{
      success: boolean;
      data: {
        id: string;
        amount: number;
        fundingDate: string;
        notes?: string;
        investment: {
          id: string;
          investmentAmount: number;
          ownershipPercentage: number;
        };
      };
    }>(`/admin/investments/${investmentId}/contributions`, data);
    return response.data.data;
  },

  async updateContribution(
    contributionId: string,
    data: { amount?: number; fundingDate?: string; notes?: string }
  ): Promise<{
    id: string;
    amount: number;
    fundingDate: string;
    notes?: string;
    investment: {
      id: string;
      investmentAmount: number;
      ownershipPercentage: number;
    };
  }> {
    const response = await api.put<{
      success: boolean;
      data: {
        id: string;
        amount: number;
        fundingDate: string;
        notes?: string;
        investment: {
          id: string;
          investmentAmount: number;
          ownershipPercentage: number;
        };
      };
    }>(`/admin/contributions/${contributionId}`, data);
    return response.data.data;
  },

  async deleteContribution(contributionId: string): Promise<{
    investment: {
      id: string;
      investmentAmount: number;
      ownershipPercentage: number;
    };
  }> {
    const response = await api.delete<{
      success: boolean;
      data: {
        investment: {
          id: string;
          investmentAmount: number;
          ownershipPercentage: number;
        };
      };
    }>(`/admin/contributions/${contributionId}`);
    return response.data.data;
  },

  // ===========================================================================
  // Deals
  // ===========================================================================

  async listDeals(options?: {
    search?: string;
    isActive?: boolean;
  }): Promise<DealWithStats[]> {
    const params = new URLSearchParams();
    if (options?.search) params.append('search', options.search);
    if (options?.isActive !== undefined) params.append('isActive', options.isActive.toString());

    const response = await api.get<{ success: boolean; data: DealWithStats[] }>(
      `/admin/deals?${params.toString()}`
    );
    return response.data.data;
  },

  async getDeal(dealId: string): Promise<DealWithStats & {
    investments: {
      id: string;
      investorId: string;
      investorName: string;
      investorEmail: string;
      profileId: string;
      profileName: string;
      investmentAmount: number;
      ownershipPercentage: number;
      totalDistributed: number;
      isActive: boolean;
      investmentDate?: string;
    }[];
    distributions: {
      id: string;
      type: string;
      totalAmount: number;
      distributionDate: string;
      paymentMethod: string;
      status: 'DRAFT' | 'PUBLISHED';
      notes?: string;
    }[];
    documents: { id: string; type: string; name: string; createdAt: string }[];
  }> {
    const response = await api.get<{ success: boolean; data: DealWithStats & {
      investments: unknown[];
      distributions: unknown[];
      documents: unknown[];
    } }>(`/admin/deals/${dealId}`);
    return response.data.data as ReturnType<typeof adminService.getDeal> extends Promise<infer T> ? T : never;
  },

  async createDeal(data: CreateDealInput): Promise<Deal> {
    const response = await api.post<{ success: boolean; data: Deal }>('/admin/deals', data);
    return response.data.data;
  },

  async updateDeal(dealId: string, data: UpdateDealInput): Promise<Deal> {
    const response = await api.put<{ success: boolean; data: Deal }>(`/admin/deals/${dealId}`, data);
    return response.data.data;
  },

  async deleteDeal(dealId: string): Promise<void> {
    await api.delete(`/admin/deals/${dealId}`);
  },

  async uploadDealImage(dealId: string, file: File): Promise<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    // Don't set Content-Type header - axios will set it automatically with the correct boundary
    const response = await api.post<{ success: boolean; data: { imageUrl: string } }>(
      `/admin/deals/${dealId}/image`,
      formData
    );
    return response.data.data;
  },

  // ===========================================================================
  // Audit Log
  // ===========================================================================

  async getAuditLog(options?: {
    type?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ events: AuditLogEntry[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.type) params.append('type', options.type);
    if (options?.userId) params.append('userId', options.userId);
    if (options?.startDate) params.append('startDate', options.startDate);
    if (options?.endDate) params.append('endDate', options.endDate);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await api.get<{
      success: boolean;
      data: AuditLogEntry[];
      total: number;
    }>(`/admin/audit-log?${params.toString()}`);

    return { events: response.data.data, total: response.data.total };
  },

  // ===========================================================================
  // Temporary Access
  // ===========================================================================

  async listTempAccess(): Promise<TempAccessUser[]> {
    const response = await api.get<{ success: boolean; data: TempAccessUser[] }>(
      '/admin/temp-access'
    );
    return response.data.data;
  },

  async createTempAccess(data: CreateTempAccessInput): Promise<TempAccessUser> {
    const response = await api.post<{ success: boolean; data: TempAccessUser }>(
      '/admin/temp-access',
      data
    );
    return response.data.data;
  },

  async updateTempAccess(userId: string, data: UpdateTempAccessInput): Promise<TempAccessUser> {
    const response = await api.put<{ success: boolean; data: TempAccessUser }>(
      `/admin/temp-access/${userId}`,
      data
    );
    return response.data.data;
  },

  async revokeTempAccess(userId: string): Promise<void> {
    await api.delete(`/admin/temp-access/${userId}`);
  },

  // ===========================================================================
  // Documents
  // ===========================================================================

  async listDocuments(options?: {
    dealId?: string;
    type?: DocumentType;
    search?: string;
    investorId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ documents: AdminDocument[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.dealId) params.append('dealId', options.dealId);
    if (options?.type) params.append('type', options.type);
    if (options?.search) params.append('search', options.search);
    if (options?.investorId) params.append('investorId', options.investorId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await api.get<{
      success: boolean;
      data: AdminDocument[];
      total: number;
    }>(`/admin/documents?${params.toString()}`);

    return { documents: response.data.data, total: response.data.total };
  },

  async uploadDocument(
    file: File,
    metadata: {
      dealId: string;
      type: DocumentType;
      name: string;
      description?: string;
      investorProfileId?: string;
      notifyInvestors?: boolean;
      visibilityMode?: 'deal' | 'specific';
      selectedInvestorIds?: string[];
    }
  ): Promise<AdminDocument> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dealId', metadata.dealId);
    formData.append('type', metadata.type);
    formData.append('name', metadata.name);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.investorProfileId) formData.append('investorProfileId', metadata.investorProfileId);
    if (metadata.notifyInvestors) formData.append('notifyInvestors', 'true');
    if (metadata.visibilityMode) formData.append('visibilityMode', metadata.visibilityMode);
    if (metadata.selectedInvestorIds && metadata.selectedInvestorIds.length > 0) {
      formData.append('selectedInvestorIds', JSON.stringify(metadata.selectedInvestorIds));
    }

    // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
    const response = await api.post<{ success: boolean; data: AdminDocument }>(
      '/admin/documents',
      formData
    );
    return response.data.data;
  },

  async uploadDocumentsBulk(
    files: File[],
    metadata: {
      investorProfileId: string;
      dealId: string;
      fileMetadata: { filename: string; type: DocumentType; name?: string }[];
    }
  ): Promise<{
    totalFiles: number;
    uploaded: number;
    errors: number;
    results: {
      uploaded: { id: string; name: string; filename: string }[];
      errors: { filename: string; error: string }[];
    };
  }> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('investorProfileId', metadata.investorProfileId);
    formData.append('dealId', metadata.dealId);
    formData.append('fileMetadata', JSON.stringify(metadata.fileMetadata));

    const response = await api.post<{
      success: boolean;
      data: {
        totalFiles: number;
        uploaded: number;
        errors: number;
        results: {
          uploaded: { id: string; name: string; filename: string }[];
          errors: { filename: string; error: string }[];
        };
      };
    }>('/admin/documents/bulk', formData);
    return response.data.data;
  },

  async updateDocument(
    documentId: string,
    data: { name?: string; description?: string; type?: DocumentType; visibilityMode?: string; selectedInvestorIds?: string[] }
  ): Promise<AdminDocument> {
    const response = await api.put<{ success: boolean; data: AdminDocument }>(
      `/admin/documents/${documentId}`,
      data
    );
    return response.data.data;
  },

  async deleteDocument(documentId: string): Promise<void> {
    await api.delete(`/admin/documents/${documentId}`);
  },

  async downloadDocument(documentId: string): Promise<Blob> {
    const response = await api.get(`/admin/documents/${documentId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async getDocumentViewUrl(documentId: string): Promise<{
    embedUrl: string;
    downloadUrl: string;
    previewUrl: string;
    proxyUrl: string;
    fileName: string;
    fileType: string;
    mimeType: string;
  }> {
    const response = await api.get<{
      success: boolean;
      data: {
        embedUrl: string;
        downloadUrl: string;
        previewUrl: string;
        fileName: string;
        fileType: string;
        mimeType: string;
      };
    }>(`/admin/documents/${documentId}/view-url`);

    // Fetch the file through our backend proxy and create a blob URL
    // This provides secure access to files via the backend
    let proxyUrl = '';
    try {
      const fileResponse = await api.get(`/admin/documents/${documentId}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([fileResponse.data], { type: response.data.data.mimeType });
      proxyUrl = URL.createObjectURL(blob);
    } catch (err) {
      console.error('Failed to fetch document via proxy:', err);
      // Fall back to embed URL if proxy fails
      proxyUrl = response.data.data.embedUrl;
    }

    return {
      ...response.data.data,
      proxyUrl,
    };
  },

  // ===========================================================================
  // Distributions
  // ===========================================================================

  async createDistribution(data: {
    dealId: string;
    type: string;
    totalAmount: number;
    distributionDate: string;
    paymentMethod: string;
    notes?: string;
    shareClassIds?: string[];
  }): Promise<{
    id: string;
    dealName: string;
    type: string;
    totalAmount: number;
    distributionDate: string;
    allocationCount: number;
  }> {
    const response = await api.post<{
      success: boolean;
      data: {
        id: string;
        dealName: string;
        type: string;
        totalAmount: number;
        distributionDate: string;
        allocationCount: number;
      };
    }>('/admin/distributions', data);
    return response.data.data;
  },

  async updateDistribution(
    distributionId: string,
    data: {
      type?: string;
      totalAmount?: number;
      distributionDate?: string;
      paymentMethod?: string;
      notes?: string;
      shareClassIds?: string[];
    }
  ): Promise<{
    id: string;
    dealName: string;
    type: string;
    totalAmount: number;
    distributionDate: string;
    paymentMethod: string;
    notes?: string;
    allocationCount: number;
  }> {
    const response = await api.put<{
      success: boolean;
      data: {
        id: string;
        dealName: string;
        type: string;
        totalAmount: number;
        distributionDate: string;
        paymentMethod: string;
        notes?: string;
        allocationCount: number;
      };
    }>(`/admin/distributions/${distributionId}`, data);
    return response.data.data;
  },

  async deleteDistribution(distributionId: string): Promise<void> {
    await api.delete(`/admin/distributions/${distributionId}`);
  },

  // ===========================================================================
  // Messages
  // ===========================================================================

  async getMessages(filter?: 'all' | 'unread' | 'sent' | 'archived'): Promise<{
    id: string;
    subject: string;
    body: string;
    fromUser?: { name: string; email: string };
    toUser?: { name: string; email: string };
    isSentByMe: boolean;
    readAt?: string;
    archivedAt?: string;
    createdAt: string;
  }[]> {
    const params = filter ? `?filter=${filter}` : '';
    const response = await api.get<{ success: boolean; data: {
      id: string;
      subject: string;
      body: string;
      fromUser?: { name: string; email: string };
      toUser?: { name: string; email: string };
      isSentByMe: boolean;
      readAt?: string;
      archivedAt?: string;
      createdAt: string;
    }[] }>(`/admin/messages${params}`);
    return response.data.data;
  },

  async markMessageRead(messageId: string): Promise<void> {
    await api.put(`/admin/messages/${messageId}/read`);
  },

  // =============================================================================
  // Investor Import
  // =============================================================================

  async downloadImportTemplate(): Promise<void> {
    const response = await api.get('/admin/investors/import/template', {
      responseType: 'blob',
    });
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Investor_Import_Template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async importInvestors(file: File): Promise<{
    created: { users: number; profiles: number; investments: number; contributions: number };
    skipped: { rowNumber: number; email: string; reason: string }[];
    warnings: { rowNumber: number; email: string; warning: string }[];
    errors: { rowNumber: number; email: string; error: string }[];
  }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<{ success: boolean; data: {
      created: { users: number; profiles: number; investments: number; contributions: number };
      skipped: { rowNumber: number; email: string; reason: string }[];
      warnings: { rowNumber: number; email: string; warning: string }[];
      errors: { rowNumber: number; email: string; error: string }[];
    } }>('/admin/investors/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // ===========================================================================
  // Share Classes
  // ===========================================================================

  async getShareClasses(dealId: string): Promise<ShareClass[]> {
    const response = await api.get<{ success: boolean; data: ShareClass[] }>(
      `/admin/deals/${dealId}/share-classes`
    );
    return response.data.data;
  },

  async getShareClass(shareClassId: string): Promise<ShareClass> {
    const response = await api.get<{ success: boolean; data: ShareClass }>(
      `/admin/share-classes/${shareClassId}`
    );
    return response.data.data;
  },

  async createShareClass(data: CreateShareClassInput): Promise<ShareClass> {
    const response = await api.post<{ success: boolean; data: ShareClass }>(
      '/admin/share-classes',
      data
    );
    return response.data.data;
  },

  async updateShareClass(shareClassId: string, data: UpdateShareClassInput): Promise<ShareClass> {
    const response = await api.put<{ success: boolean; data: ShareClass }>(
      `/admin/share-classes/${shareClassId}`,
      data
    );
    return response.data.data;
  },

  async deleteShareClass(shareClassId: string): Promise<void> {
    await api.delete(`/admin/share-classes/${shareClassId}`);
  },

  // ===========================================================================
  // Waterfall Tiers
  // ===========================================================================

  async createWaterfallTier(shareClassId: string, data: CreateWaterfallTierInput): Promise<WaterfallTier> {
    const response = await api.post<{ success: boolean; data: WaterfallTier }>(
      `/admin/share-classes/${shareClassId}/tiers`,
      data
    );
    return response.data.data;
  },

  async createDefaultTiers(shareClassId: string): Promise<WaterfallTier[]> {
    const response = await api.post<{ success: boolean; data: WaterfallTier[] }>(
      `/admin/share-classes/${shareClassId}/tiers/default`
    );
    return response.data.data;
  },

  async updateWaterfallTier(tierId: string, data: UpdateWaterfallTierInput): Promise<WaterfallTier> {
    const response = await api.put<{ success: boolean; data: WaterfallTier }>(
      `/admin/tiers/${tierId}`,
      data
    );
    return response.data.data;
  },

  async deleteWaterfallTier(tierId: string): Promise<void> {
    await api.delete(`/admin/tiers/${tierId}`);
  },

  // ===========================================================================
  // Waterfall Analysis
  // ===========================================================================

  async getWaterfallAnalysis(dealId: string): Promise<WaterfallAnalysisData> {
    const response = await api.get<{ success: boolean; data: WaterfallAnalysisData }>(
      `/admin/deals/${dealId}/waterfall-analysis`
    );
    return response.data.data;
  },

  // ===========================================================================
  // Escrow Management
  // ===========================================================================

  async getEscrowBalance(dealId: string): Promise<EscrowBalance> {
    const response = await api.get<{ success: boolean; data: EscrowBalance }>(
      `/admin/deals/${dealId}/escrow`
    );
    return response.data.data;
  },

  async releaseEscrow(dealId: string): Promise<{ releasedCount: number; releasedTotal: number }> {
    const response = await api.post<{ success: boolean; data: { releasedCount: number; releasedTotal: number } }>(
      `/admin/deals/${dealId}/escrow/release`
    );
    return response.data.data;
  },

  // ===========================================================================
  // Clawback Calculation
  // ===========================================================================

  async getClawbackStatus(dealId: string): Promise<ClawbackResult | null> {
    const response = await api.get<{ success: boolean; data: ClawbackResult | null }>(
      `/admin/deals/${dealId}/clawback`
    );
    return response.data.data;
  },

  async simulateClawback(dealId: string): Promise<ClawbackResult> {
    const response = await api.post<{ success: boolean; data: ClawbackResult }>(
      `/admin/deals/${dealId}/clawback/simulate`
    );
    return response.data.data;
  },

  async executeClawback(dealId: string): Promise<ClawbackResult> {
    const response = await api.post<{ success: boolean; data: ClawbackResult }>(
      `/admin/deals/${dealId}/clawback/execute`
    );
    return response.data.data;
  },

  // ===========================================================================
  // Lookback / True-Up
  // ===========================================================================

  async getLookbackStatus(dealId: string): Promise<LookbackResult | null> {
    const response = await api.get<{ success: boolean; data: LookbackResult | null }>(
      `/admin/deals/${dealId}/lookback`
    );
    return response.data.data;
  },

  async simulateLookback(dealId: string): Promise<LookbackResult> {
    const response = await api.post<{ success: boolean; data: LookbackResult }>(
      `/admin/deals/${dealId}/lookback/simulate`
    );
    return response.data.data;
  },

  // ===========================================================================
  // Preferred Equity Tranches
  // ===========================================================================

  async getPreferredEquityTranches(dealId: string): Promise<PreferredEquityTrancheData[]> {
    const response = await api.get<{ success: boolean; data: PreferredEquityTrancheData[] }>(
      `/admin/deals/${dealId}/preferred-equity`
    );
    return response.data.data;
  },

  async createPreferredEquityTranche(dealId: string, data: CreatePreferredEquityInput): Promise<PreferredEquityTrancheData> {
    const response = await api.post<{ success: boolean; data: PreferredEquityTrancheData }>(
      `/admin/deals/${dealId}/preferred-equity`,
      data
    );
    return response.data.data;
  },

  async updatePreferredEquityTranche(trancheId: string, data: Partial<CreatePreferredEquityInput> & { isActive?: boolean }): Promise<PreferredEquityTrancheData> {
    const response = await api.put<{ success: boolean; data: PreferredEquityTrancheData }>(
      `/admin/preferred-equity/${trancheId}`,
      data
    );
    return response.data.data;
  },

  async deletePreferredEquityTranche(trancheId: string): Promise<void> {
    await api.delete(`/admin/preferred-equity/${trancheId}`);
  },

  // ===========================================================================
  // Waterfall Excel Exports
  // ===========================================================================

  async exportDealWaterfall(dealId: string): Promise<void> {
    try {
      console.log('[exportDealWaterfall] Starting export for deal:', dealId);
      const response = await api.get(`/admin/deals/${dealId}/export-waterfall?t=${Date.now()}`, {
        responseType: 'blob',
      });
      console.log('[exportDealWaterfall] Response received, content-type:', response.headers['content-type']);

      // Check if response is actually an error (JSON instead of Excel)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // Parse the error from the blob
        const text = await response.data.text();
        console.error('[exportDealWaterfall] Server returned JSON error:', text);
        const errorData = JSON.parse(text);
        throw new Error(errorData.error || 'Export failed');
      }

      // Create download link
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Deal_Waterfall_Export.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }
      console.log('[exportDealWaterfall] Creating download for:', filename);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      console.log('[exportDealWaterfall] Download triggered successfully');
    } catch (error: any) {
      console.error('[exportDealWaterfall] Error:', error);
      // Try to extract error message from blob response
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          console.error('[exportDealWaterfall] Error blob text:', text);
          const errorData = JSON.parse(text);
          throw new Error(errorData.error || errorData.message || 'Export failed');
        } catch (parseError) {
          console.error('[exportDealWaterfall] Could not parse error blob');
        }
      }
      throw error;
    }
  },

  async exportInvestmentWaterfall(investmentId: string): Promise<void> {
    const response = await api.get(`/admin/investments/${investmentId}/export-waterfall?t=${Date.now()}`, {
      responseType: 'blob',
    });
    // Create download link
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'Investment_Waterfall_Export.xlsx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (match) filename = match[1];
    }
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // ===========================================================================
  // Team Members (Viewers)
  // ===========================================================================

  async listTeamMembers(): Promise<TeamMemberResponse[]> {
    const response = await api.get<{ success: boolean; data: TeamMemberResponse[] }>(
      '/admin/team'
    );
    return response.data.data;
  },

  async getTeamMember(id: string): Promise<TeamMemberResponse> {
    const response = await api.get<{ success: boolean; data: TeamMemberResponse }>(
      `/admin/team/${id}`
    );
    return response.data.data;
  },

  async createTeamMember(data: CreateTeamMemberInput): Promise<{ id: string; userId: string; email: string; accessLevel: string }> {
    const response = await api.post<{
      success: boolean;
      data: { id: string; userId: string; email: string; accessLevel: string };
    }>('/admin/team', data);
    return response.data.data;
  },

  async updateTeamMember(id: string, data: UpdateTeamMemberInput): Promise<void> {
    await api.put(`/admin/team/${id}`, data);
  },

  async deleteTeamMember(id: string): Promise<void> {
    await api.delete(`/admin/team/${id}`);
  },

  async resendTeamInvitation(id: string): Promise<void> {
    await api.post(`/admin/team/${id}/resend-invitation`);
  },

  async getAccessLevels(): Promise<AccessLevelPreset[]> {
    const response = await api.get<{ success: boolean; data: AccessLevelPreset[] }>(
      '/admin/team/access-levels'
    );
    return response.data.data;
  },

  async getMyPermissions(): Promise<MyPermissionsResponse> {
    const response = await api.get<{ success: boolean; data: MyPermissionsResponse }>(
      '/admin/team/my-permissions'
    );
    return response.data.data;
  },
};

// =============================================================================
// Team Member Types
// =============================================================================

export type TeamAccessLevel = 'FULL_ACCESS' | 'ASSET_MANAGER' | 'INVESTOR_RELATIONS' | 'DEAL_ACCESS' | 'VIEW_ONLY';

export interface TeamPermissions {
  accessLevel: TeamAccessLevel;
  dealScopeMode: 'all' | 'specific'; // 'all' = unrestricted, 'specific' = only assignedDealIds
  areas: {
    dashboard: { view: boolean };
    investors: { view: boolean; create: boolean; edit: boolean; delete: boolean; sensitive: boolean };
    deals: { view: boolean; create: boolean; edit: boolean; delete: boolean };
    distributions: { view: boolean; create: boolean; edit: boolean; delete: boolean };
    documents: { view: boolean; upload: boolean; edit: boolean; delete: boolean };
    communications: { view: boolean; create: boolean; delete: boolean };
    fundraising: { view: boolean; create: boolean; edit: boolean; delete: boolean };
    accounting: { view: boolean; manage: boolean };
    auditLog: { view: boolean };
  };
  assignedDealIds?: string[];
}

export interface TeamMemberResponse {
  id: string;
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  name: string;
  isActive: boolean;
  lastLoginAt: string | null;
  accessLevel: TeamAccessLevel;
  permissions: TeamPermissions;
  assignedDeals: { id: string; name: string; status: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateTeamMemberInput {
  email: string;
  firstName: string;
  lastName: string;
  accessLevel: TeamAccessLevel;
  permissions?: TeamPermissions;
  assignedDealIds?: string[];
}

export interface UpdateTeamMemberInput {
  accessLevel?: TeamAccessLevel;
  permissions?: TeamPermissions;
  assignedDealIds?: string[];
}

export interface AccessLevelPreset {
  value: TeamAccessLevel;
  label: string;
  description: string;
  permissions: TeamPermissions;
}

export interface MyPermissionsResponse {
  role: 'ADMIN' | 'TEAM';
  permissions: TeamPermissions;
  isAdmin: boolean;
}

export default adminService;
